import { z } from 'zod';
import { experienceContentSchema, experienceLocalIdSchema, richTextSchema } from './experience.js';
import { noyauPromptFingerprintSchema } from './noyau-session.js';
import { noyauCardIdSchema, noyauCodeSchema, noyauDeterminationStateSchema } from './noyau.js';
import { personaCardPresentationSchema } from './persona.js';
import { objectiveStatusSchema, protocolLocalIdSchema } from './protocol.js';
import { replayFilterSchema, replayMarkSchema, replayNpcFilterSchema } from './replay-mark.js';
import { sessionEntrySchema, sessionStatusSchema, sessionSummarySchema } from './session.js';
import { sessionConversationMessageSchema } from './turn.js';
import { sessionOriginSchema } from './versioning.js';

/**
 * PLAYING a session (refonte modulaire §5.1 to §5.5, §9.5, §12.1) - the half of
 * the session only the Expériences lot reads.
 *
 * `schemas/session.ts` is the contract the two lots SHARE, and its own header
 * says the shapes only one side reads « stay in their lot »: the player's view
 * of a page is one of them, and so is the operator's list of §12.1, whose row
 * (`sessionSummarySchema`) that file already holds. They are here so the shared
 * contract keeps saying exactly what both lots must agree on, and nothing more.
 *
 * Nothing of #50 is redefined below: the stored statuses, the derived one,
 * `SESSION_TTL_HOURS`, the frozen resolution, the variables, the row and the SSE
 * frames all come from `session.ts` unchanged.
 */

/* ---------------------------------------------------------------- the start */

/**
 * `POST /api/l/{secret}/sessions` - which entry of the link is being opened.
 *
 * Null when the link carries ONE entry: « ouvrir le lien démarre directement »
 * (§5.1), and the browser that opened a single-entry link has nothing to name.
 * With several entries the choice page names one, and an id that is not among
 * the link's entries is refused rather than silently taking the first - a link
 * whose second entry stopped resolving must not quietly start the first.
 */
export const sessionStartSchema = z
  .object({
    experienceId: z.string().min(1).nullable().default(null),
  })
  .strict();
export type SessionStart = z.infer<typeof sessionStartSchema>;

/**
 * What a started session hands back: its token, and nothing else.
 *
 * The token IS the address of §5.2 (`/s/<jeton>`) and the person's only
 * credential. The browser navigates there and reads the session by
 * `GET /api/s/{token}`, so answering the whole view here would be a second
 * shape to keep in phase for one round trip saved.
 */
export const sessionStartedSchema = z.object({ token: z.string().min(1) }).strict();
export type SessionStarted = z.infer<typeof sessionStartedSchema>;

/* -------------------------------------------------------------- the refusals */

/**
 * Why a gesture of the session is refused. All of them 409, at the régime of
 * every other business refusal of this repo: the CONTRACT's own body, and the
 * screen words each code itself.
 *
 * - `entry-unknown` - the body named an experience the link does not carry.
 * - `entry-unresolved` - the cascade of §5.2 does not resolve (a retired object,
 *   an empty `production` pointer). Refused in `production` AND in
 *   `development`: a session frozen on half a cascade is a session the engine
 *   could never replay.
 * - `engine-missing` - the seam of §9.5 on a `production` link, before the
 *   engine exists. « Une personne réelle ne doit jamais voir la couture. »
 * - `session-over` - the session is finished, closed, interrupted or past its
 *   24 hours (§5.3): « le moteur refuse tout tour et toute avancée de page ».
 * - `page-mismatch` - the advance names a page the session is not on. A stale
 *   tab, or two tabs on one session; taking it would replay a page.
 * - `values-refused` - a required block already appeared and holds nothing, or
 *   an email that is not one (§9.2, the server half of the double check).
 * - `turn-in-flight` - a turn of this session is already being played (§9.5).
 *   The POST of a message answers at once and the two model calls run behind
 *   it, so a second message arriving before the first has landed would put two
 *   scribes on one state and write two turns under one rank. Refused rather
 *   than queued: somebody who double-taps « Envoyer » meant to send once, and a
 *   queue would answer a question they have already stopped waiting for.
 * - `noyau-pending` - the advance leads to a page that depends on the noyau,
 *   and the determination is still running (noyau.md §4.6, N5). The session
 *   does not move; the button stays on « Un instant... », and the client
 *   REPLAYS the advance at the `noyau` frame of the channel. A refusal and not
 *   an open request: two calls of up to seven minutes each (issue #166) are
 *   far beyond what a POST should hold behind nginx.
 * - `choice-refused` - the advance leaves a page of CHOICE without naming one
 *   of the two cards the page served (noyau.md §5.1, issue #151): no `cardId`,
 *   or an identifier this session was never shown. « Choisir ce texte » is the
 *   page's only way out, so a card is what leaving it means - and a card
 *   nobody was shown is a retained noyau nobody chose. Nothing is written.
 * - `session-in-trash` - an EXPORT of a session in the trash (§12.5, issue
 *   #173, H116), in any of its formats; the assistant's reads refuse it under
 *   the same code (§20.5). The screen still opens it under its banner, but a
 *   file that left after the erasure is a leak the thirty-day purge never
 *   catches. A refusal and not a 404: the operator knows the session exists,
 *   and the screen words « Restaurez la session pour l'exporter ».
 * - `feedback-already-sent` - the SECOND submit of the feedback page (issue
 *   #176, retour-experience.md §5): one feedback per session, so the page
 *   reopened - or a second tab - gets this refusal and rereads the session,
 *   which shows the phrase of thanks. Nothing is written.
 * - `no-protocol-page` - the INTERRUPTION of a session whose frozen experience
 *   has no protocol page (issue #199, interruption.md §4.3): no review is
 *   possible for it, so the gesture does not exist. Nothing is written.
 * - `session-from-prod` - the INTERRUPTION, on the atelier, of a session the
 *   synchronisation brought up from the vitrine (§5): it is interrupted on the
 *   prod, where it plays. Nothing is written.
 */
export const sessionRefusalCodeSchema = z.enum([
  'entry-unknown',
  'entry-unresolved',
  'engine-missing',
  'session-over',
  'page-mismatch',
  'values-refused',
  'turn-in-flight',
  'noyau-pending',
  'choice-refused',
  'session-in-trash',
  'feedback-already-sent',
  'no-protocol-page',
  'session-from-prod',
]);
export type SessionRefusalCode = z.infer<typeof sessionRefusalCodeSchema>;

/** Which block refuses the advance, and why (§9.2). */
export const sessionValueProblemSchema = z
  .object({
    blockId: z.string().min(1),
    kind: z.enum(['required', 'email']),
  })
  .strict();
export type SessionValueProblem = z.infer<typeof sessionValueProblemSchema>;

export const sessionErrorSchema = z
  .object({
    error: z.string(),
    code: sessionRefusalCodeSchema,
    /** Empty on every code but `values-refused`, where it names each block. */
    problems: z.array(sessionValueProblemSchema).default([]),
  })
  .strict();
export type SessionError = z.infer<typeof sessionErrorSchema>;

/* ----------------------------------------------------------------- the view */

/**
 * What the reader holds for a block: the text of an Input, the tick of a
 * Checkbox - keyed by BLOCK id, never by variable name (§8.6).
 *
 * The session STORES `SessionVariables` (the value plus the name frozen beside
 * it); this is the same record with the names dropped, because that is exactly
 * what `PageReader` takes and what the advance sends back.
 */
export const sessionValuesSchema = z.record(z.string().min(1), z.union([z.string(), z.boolean()]));
export type SessionValues = z.infer<typeof sessionValuesSchema>;

/**
 * The persona of the session, as the PERSON may see it (§9.5, §10.1).
 *
 * Only the presentation crosses: « un persona nommé montre son prénom dans
 * l'en-tête et ses bulles, un persona anonyme n'a ni nom ni identité affichée ».
 * The direction, the overlay, the tone and the involvement level are the
 * engine's business and never leave the server - they are what the interviewer
 * is told, not what the person is shown.
 */
export const sessionPersonaSchema = personaCardPresentationSchema;
export type SessionPersona = z.infer<typeof sessionPersonaSchema>;

/**
 * What the protocol page does in THIS session (§9.5).
 *
 * - `skipped` - the page carries no protocol, so it is never served at all.
 * - `seam` - the sober card « Cette conversation arrive bientôt » and its
 *   « Continuer », visible on a `development` link only, until the Protocoles
 *   lot puts the real conversation in its place.
 * - `conversation` - the engine of §8 plays the page: bubbles, a multi-line
 *   input, and the words of the interviewer arriving on the SSE channel.
 *
 * `conversation` was deliberately absent until the engine existed, and the day
 * it landed is this one (issue #63): the union gains it, and a browser loaded
 * before that deploy fails the parse loudly instead of drawing a seam over a
 * live conversation. What ANSWERS `conversation` is the server's business
 * (issue #64): `sessionProtocolMode` in `session/freeze.ts` still answers
 * `seam` while `PROTOCOL_ENGINE_AVAILABLE` is false, so nothing in the seam of
 * §9.5 moves before the turn is played server side.
 */
export const sessionProtocolModeSchema = z.enum(['skipped', 'seam', 'conversation']);
export type SessionProtocolMode = z.infer<typeof sessionProtocolModeSchema>;

/**
 * One card of a choice as the PERSON's browser receives it (noyau.md §5.1,
 * N28): the opaque identifier and the text, and NOTHING else - neither the
 * noyau it stands for, nor its triplet, nor its colour, nor the word
 * « principal », « second » or « opposé ». The advance names the card taken by
 * this identifier, and the server alone knows which noyau it is.
 *
 * The text is the rich text of the version of the texts FROZEN on the session
 * (§3.5): the first text on the first choice, the second text on the second.
 */
export const sessionNoyauCardSchema = z
  .object({
    cardId: noyauCardIdSchema,
    text: richTextSchema,
  })
  .strict();
export type SessionNoyauCard = z.infer<typeof sessionNoyauCardSchema>;

/**
 * What the reader knows of the noyau sequence (noyau.md §5, §4.5), and no more.
 *
 * - `determination` - its state, null while there is none: an experience
 *   without a Noyau block, or a protocol not closed yet. Enough for a reader to
 *   know whether the pages that depend on the noyau will play (§4.5).
 * - `choice` - on a page of choice, its block and its two cards IN THE ORDER
 *   SHOWN, drawn at the first arrival and frozen for a reprise; null anywhere
 *   else. Never a code (N28), and never two cards under one identifier: the
 *   advance names the card taken by it, and one identifier for two cards would
 *   let the server read either.
 * - `retained` - the RETAINED noyau, from the first choice on, null before and
 *   when the sequence was skipped. Its code travels because the reader renders
 *   `@noyau` and evaluates a condition on it (§3.6, H72) - and by then the
 *   person has taken that card: the code says what they chose, not whether it
 *   was the principal or the second, which never leave the server.
 * - `portrait` - the portrait of the retained noyau in the frozen texts, null
 *   when there is none to show; the page that shows it is a Noyau block in
 *   portrait mode, rendered by the pure function of the Texte block.
 */
export const sessionNoyauSchema = z
  .object({
    determination: noyauDeterminationStateSchema.nullable(),
    choice: z
      .object({
        blockId: experienceLocalIdSchema,
        cards: z
          .tuple([sessionNoyauCardSchema, sessionNoyauCardSchema])
          .refine(([first, second]) => first.cardId !== second.cardId, {
            message: 'The two cards of a choice carry two different identifiers.',
          }),
      })
      .strict()
      .nullable(),
    retained: noyauCodeSchema.nullable(),
    portrait: richTextSchema.nullable(),
  })
  .strict();
export type SessionNoyau = z.infer<typeof sessionNoyauSchema>;

/**
 * `GET /api/s/{token}` - the whole of what the player's browser needs.
 *
 * The FROZEN content and not the current one (§5.2): a version passed to
 * production while somebody plays changes nothing under them. The status is
 * DERIVED (§5.3) - `expired` is never stored - and `expiresAt` travels so the
 * sober screen of §5.3 can be drawn without the browser knowing the 24 hours.
 *
 * `content` is the experience version entire, theme included, because the
 * reader needs the theme of the experience AND the page's own overrides, and
 * because a `{{champ:<blockId>}}` of a PREVIOUS page resolves against a block
 * this page does not hold. Serving one page at a time would have meant a round
 * trip per variable.
 */
export const sessionViewSchema = z
  .object({
    status: sessionStatusSchema,
    /**
     * The couple the link entry resolved (§5.1), as the session recorded it.
     *
     * It travels because « Recommencer » (§5.3) has to reopen the SAME
     * experience, and the door only knows which session this browser
     * remembers - not what it plays. No secret is in it: the id and the tag
     * are what the choice page's own POST already names.
     */
    entry: sessionEntrySchema,
    startedAt: z.string().datetime(),
    expiresAt: z.string().datetime(),
    endedAt: z.string().datetime().nullable(),
    /** The excuse of the engine on `closed` (§5.3), null on every other status. */
    closeReason: z.string().min(1).nullable(),
    /** The page to play. Null only before the first page is served. */
    currentPageId: z.string().min(1).nullable(),
    values: sessionValuesSchema,
    content: experienceContentSchema,
    persona: sessionPersonaSchema.nullable(),
    protocolMode: sessionProtocolModeSchema,
    /**
     * The bubbles already played on the protocol page (§9.5), oldest first, so
     * a reprise (§5.3) or a reconnection redraws the whole thread without
     * replaying a turn - the channel carries deltas, the REST view carries the
     * truth.
     *
     * OPTIONAL, and that is the seam: the server of #60 answers a view without
     * it and stays valid, #64 fills it the day the engine plays the page. A
     * reader spells it `view.conversation ?? []`, which is also the right
     * answer for every `skipped` and `seam` session - there is no conversation
     * to draw.
     */
    conversation: z.array(sessionConversationMessageSchema).optional(),
    /**
     * The matter of each objective as the PERSON may read it (§8.6, grill
     * Variables D4): the LIVE extracts, by objective id, in the person's own
     * words. A page after the protocol renders `@objectif` from this - the
     * extracts alone, one per line, never the name or the status that §9.5
     * forbids showing (`resolvePersonVariable`).
     *
     * OPTIONAL at the régime of `conversation`: the server of before the grill
     * answers a view without it and stays valid; the reader then renders such
     * an objective as nothing, which is also the right answer for a session
     * whose protocol found no matter.
     */
    matter: z.record(z.string().min(1), z.array(z.string())).optional(),
    /**
     * The direction of each criterion the frozen protocol cites, by id: what a
     * `@critère` on a page renders to the person (§8.6). Optional, same régime.
     */
    criterionDirections: z.record(z.string().min(1), z.string()).optional(),
    /**
     * The STATUS of each objective the session met, by objective id, off the
     * last turn's snapshot - what a display condition on an objective reads
     * (§9.2 of the refonte, H68, H72). An objective with no key is `vide`, so
     * « n'est pas réussi » (H71).
     *
     * It travels because the reader evaluates the conditions of a page with the
     * very function the server plays at the advance (`isBlockDisplayed`), on the
     * very values: a reader that held no status would mask a block the server
     * shows, and refuse nothing where the server refuses a required field the
     * person cannot see. It is never SHOWN - §9.5 forbids showing a status to
     * the person, not carrying it - and it is readable in the browser's tools,
     * which N28 does not forbid: N28 is about the codes of the noyau.
     *
     * OPTIONAL at the régime of `matter`: a server of before issue #150 answers
     * a view without it, which a reader reads as every objective empty.
     */
    objectives: z.record(protocolLocalIdSchema, objectiveStatusSchema).optional(),
    /**
     * The noyau sequence as the person may see it (noyau.md §5, N28).
     *
     * OPTIONAL at the régime of `conversation` and `matter`: the server of
     * before issue #145 answers a view without it and stays valid. Served since
     * issue #151 on every session whose FROZEN experience carries a Noyau block,
     * and absent on every other - a reader spells it `view.noyau ?? null`,
     * which is also the right answer for those.
     */
    noyau: sessionNoyauSchema.optional(),
    /**
     * When the feedback of the page of retour was SENT (retour-experience.md §5),
     * null while it was not. OPTIONAL at the régime of `noyau`: a server of
     * before issue #176 answers a view without it, which a reader reads as
     * « not sent » - `view.feedbackSubmittedAt ?? null`. A reader that finds it
     * set shows the phrase of thanks in place of the page's blocks.
     */
    feedbackSubmittedAt: z.string().datetime().nullable().optional(),
  })
  .strict();
export type SessionView = z.infer<typeof sessionViewSchema>;

/* -------------------------------------------------------------- the advance */

/**
 * `POST /api/s/{token}/advance` - leaving the page the session is on.
 *
 * `pageId` is named rather than assumed: two tabs on one session, or a tab left
 * open across a reprise, would otherwise advance twice from one press.
 *
 * `pressedBlockId` is the Bouton the person pressed, or null when the page's
 * automatic duration ran out (§9.2). It carries TWO things the server cannot
 * guess: which temps had already appeared - only the obligatoires of §9.2 that
 * appeared may refuse the advance (H30) - and the label of the choice, for the
 * `{{choix:<pageId>}}` of §8.6.
 *
 * `cardId` is the card CONFIRMED on a page of choice (noyau.md §5.1): the
 * opaque identifier the view served, never a noyau - the server maps it back.
 * `pressedBlockId` is then the Noyau block, whose « Choisir ce texte » is the
 * page's only way out. OPTIONAL rather than defaulted, because it is absent from
 * every advance but that one: a stale tab of before issue #145 posts none, and
 * the only thing a missing card can mean is « not a choice ».
 */
export const sessionAdvanceSchema = z
  .object({
    pageId: z.string().min(1),
    values: sessionValuesSchema,
    pressedBlockId: z.string().min(1).nullable().default(null),
    cardId: noyauCardIdSchema.optional(),
  })
  .strict();
export type SessionAdvance = z.infer<typeof sessionAdvanceSchema>;

/* ---------------------------------------------------- the operator's list */

/**
 * Where a session stands INSIDE its protocol page (§12.1, §12.2) - « page
 * Protocole · étape 2 · Contexte de vie, tour 3 ».
 *
 * Read off the LAST turn of the session and resolved against the frozen
 * protocol, which is why it is served rather than computed by the screen: a
 * turn records a `stepId` and nothing else, and the rank and the name of that
 * step live in a version the list does not hold.
 *
 * Null for every session that has played no turn, whatever its status - one
 * stopped before the protocol page, one whose protocol page was skipped, and a
 * conversation that has not had its first turn all look the same from here, and
 * all three are « no step ».
 */
export const sessionStepPositionSchema = z
  .object({
    stepId: protocolLocalIdSchema,
    /** 1-based rank in the frozen protocol, the number the screens print. */
    index: z.number().int().positive(),
    name: z.string().min(1),
    /** Turns played on the CURRENT attempt (§8.3), the counter the cap is read on. */
    turnInStep: z.number().int().nonnegative(),
    /** The pass of the step, 1-based; printed only when it is above one (§12.2). */
    attempt: z.number().int().positive(),
  })
  .strict();
export type SessionStepPosition = z.infer<typeof sessionStepPositionSchema>;

/**
 * « Le nom de la personne dans Sessions » (§9.2 and §12.1 of the refonte, H75):
 * the value the session typed into the one-line Input its FROZEN experience
 * marks, read by `isPersonNameInput` - the one reading of the flag.
 *
 * The block travels beside the value, and it is not decoration: the MCP masks
 * the value of every Input whose format is `email` (§20.5), and a name typed
 * into such a field is an email all the same - the mask needs to know which
 * block it came from to say so. Null when the experience marks no field, and
 * when the session left it empty or never reached it.
 */
export const sessionPersonNameSchema = z
  .object({
    blockId: z.string().min(1),
    value: z.string().min(1),
  })
  .strict();
export type SessionPersonName = z.infer<typeof sessionPersonNameSchema>;

/**
 * The FIVE codes of the noyau a line carries (noyau.md §9.2, §10; §12.1 and
 * §20.5 of the refonte) - what lets a chat count an agreement over fifty
 * sessions without opening them one by one.
 *
 * - `principal`, `second` - the ranking of the determination, null unless it
 *   was rendered;
 * - `firstChoice` - since issue #169, the noyau of the card confirmed at the
 *   FIRST choice (N76): beyond the gap threshold the retained noyau is no
 *   longer that card (§3.8), and the agreement of §13 is counted on the card;
 * - `retained` - the RETAINED noyau, by the rule of `noyauRetention`: the card
 *   of the first choice within its threshold, the principal beyond it. The pill
 *   of the list, and what its eighth filter matches (N26, N75);
 * - `secondChoice` - the noyau of the card confirmed at the SECOND choice, the
 *   retained one or its opposite.
 *
 * And, since #154, the FINGERPRINT of the system the LAST call of the
 * determination was served (noyau.md §4.9, §10, N40): what lets a chat group
 * the agreement by reading without opening the sessions - with no filter of
 * its own. Null with no call yet, and on a call recorded before #154.
 *
 * Nothing here is stored: all of it is READ off the determination and the
 * choices, never a second copy of either. The operator's reading, and not the
 * person's: N28 keeps the codes out of the PERSON's browser, and this row is
 * behind the operator door.
 */
export const sessionNoyauCodesSchema = z
  .object({
    principal: noyauCodeSchema.nullable(),
    second: noyauCodeSchema.nullable(),
    firstChoice: noyauCodeSchema.nullable(),
    retained: noyauCodeSchema.nullable(),
    secondChoice: noyauCodeSchema.nullable(),
    promptFingerprint: noyauPromptFingerprintSchema.nullable(),
  })
  .strict();
export type SessionNoyauCodes = z.infer<typeof sessionNoyauCodesSchema>;

/**
 * One place the search found a session (recherche-sessions.md §5, §8), outside
 * the columns the line already shows.
 *
 * - `field`: the value of a page's field; `label` is the block's label in the
 *   FROZEN version, null when that version can no longer be read (§10).
 * - `reply`: the person's answer at `turnIndex` (0-based, like the turn's own
 *   `turnIndex`); `label` is null.
 * - `session-id`: the session's identifier; `label` and `turnIndex` are null.
 *
 * `spans` are the passages of the search words inside `excerpt`, `[start, end)`
 * in positions of the excerpt string, ellipses included (`searchExcerpt`).
 */
export const sessionSearchHitSchema = z
  .object({
    kind: z.enum(['field', 'reply', 'session-id']),
    label: z.string().nullable(),
    turnIndex: z.number().int().nonnegative().nullable(),
    excerpt: z.string(),
    spans: z.array(z.tuple([z.number().int().nonnegative(), z.number().int().nonnegative()])),
  })
  .strict();
export type SessionSearchHit = z.infer<typeof sessionSearchHitSchema>;

/**
 * One line of Sessions (§12.1): the row of #50, the names a screen shows and
 * identifiers do not spell, and the figures §12.1 asks a line to carry.
 *
 * The names are read at the LIST and not frozen on the row: an experience
 * renamed since is one object with one current name, and Sessions is the
 * operator's screen. What IS frozen stays frozen - the version played, the tag,
 * the cascade - because those are what the session actually ran on.
 *
 * `protocolName` and `personaName` are the « ligne de référents » the maquette
 * puts under the experience, and they are not decoration: three of the seven
 * filters of §12.1 bear on those objects, and a filtered list whose lines do not
 * say why they are there is a list nobody trusts.
 */
export const sessionRowSchema = z
  .object({
    ...sessionSummarySchema.shape,
    /** Null when the experience was deleted outright - nothing deletes one today. */
    experienceName: z.string().min(1).nullable(),
    /** « Version jouée » (§12.1): the number of the frozen experience version. */
    experienceVersionNumber: z.number().int().positive().nullable(),
    /** The link's operator-facing name (§11.4); null for a session no link opened. */
    linkName: z.string().min(1).nullable(),
    /** The name of `currentPageId` inside the frozen version. */
    currentPageName: z.string().min(1).nullable(),
    /**
     * The protocol the session froze, resolved from the frozen VERSION to the
     * object it belongs to; null when the page carried none, and null too when
     * nothing can resolve the version any more.
     *
     * The id travels beside the name because it is what the filter of §12.1 is
     * keyed on: a chip that says « Entretien fondateur » has to post an
     * identifier, and a screen that had only the name would be matching strings.
     */
    protocolId: z.string().min(1).nullable(),
    protocolName: z.string().min(1).nullable(),
    protocolVersionNumber: z.number().int().positive().nullable(),
    /** The persona the session froze, resolved the same way; null when none. */
    personaId: z.string().min(1).nullable(),
    personaName: z.string().min(1).nullable(),
    personaVersionNumber: z.number().int().positive().nullable(),
    /**
     * The criteria the frozen protocol cites (§5.2), as OBJECTS - what « critère »
     * filters on.
     *
     * The ids and not the names: §12.1 puts the protocol and the persona on the
     * referents line and stops there, and a line naming eight criteria would be
     * a line nobody reads. What this carries is the answer to « est-ce que cette
     * session a joué ce critère », which the freeze already wrote down.
     */
    criterionIds: z.array(z.string().min(1)),
    /**
     * « Durée » (§12.1), in milliseconds: from the start to the last thing that
     * actually happened, and never to `now`.
     *
     * Measured on the session's own traces - its end, its last turn, its last
     * page - so the figure is STABLE: a tab left open all afternoon does not
     * turn a four-minute conversation into a four-hour one, and an expired
     * session reads the minutes it was used rather than the 24 hours it was
     * allowed.
     */
    durationMs: z.number().int().nonnegative(),
    /** Where it stands in the protocol, for the context line under the status. */
    currentStep: sessionStepPositionSchema.nullable(),
    /** The person's name, when the frozen experience marks a field for it (H75). */
    personName: sessionPersonNameSchema.nullable(),
    /**
     * The five codes of the noyau (issue #152, #169), null on a session with no
     * determination at all - an experience without a Noyau block, or a
     * protocol not closed yet.
     */
    noyauCodes: sessionNoyauCodesSchema.nullable(),
    /**
     * The principal noyau of the most recent trial run from the session
     * (noyau.md §9.4, issue #170), null when it ran none or none rendered. Not
     * a retained noyau: the list shows it in that column only when the line
     * retained none, and marks it as a trial.
     */
    noyauTrial: noyauCodeSchema.nullable(),
    /**
     * Who set something in the REVIEW of the session (relecture.md §13, issue
     * #187), the distinct emails in order of first appearance - the mark
     * « relecture · patrick, alice ». Empty when the review holds nothing, and
     * on a row written before the review existed. The reviewer's noyau is NOT
     * read here: the column Noyau stays the machine's.
     */
    reviewAuthors: z.array(z.string().min(1)).default([]),
    /**
     * The mark of a REPLAY (rejeu.md sections 11.3 and 12, issue #193):
     * `{ mode, sourceKind, sourceLabel }` on a session Claude played in the
     * place of a person, null on every other. A replay has no link, so this is
     * what the column Lien shows in its place.
     *
     * OPTIONAL at the régime of `feedbackSubmittedAt`: a row built by a server
     * of before the replay has none and reads `null` - `row.replay ?? null`.
     */
    replay: replayMarkSchema.nullable().optional(),
    /**
     * Where the search found the line OUTSIDE its visible columns
     * (recherche-sessions.md §5 and §8), in the order of §5, at most twenty.
     * Absent without a search, and on a row of before: same régime as `replay`.
     */
    searchHits: z.array(sessionSearchHitSchema).max(20).optional(),
    /** How many places there are in all, `searchHits` being capped at twenty. Absent without a search. */
    searchHitCount: z.number().int().nonnegative().optional(),
  })
  .strict();
export type SessionRow = z.infer<typeof sessionRowSchema>;

export const sessionListSchema = z.object({ sessions: z.array(sessionRowSchema) }).strict();
export type SessionList = z.infer<typeof sessionListSchema>;

/** A day of the period filter, `YYYY-MM-DD`, read in UTC like every stored stamp. */
export const sessionDaySchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);

/**
 * The eight filters of §12.1, plus the ninth of the review (`review`,
 * relecture.md §13, issue #187), the tenth of the replay (`replay`, rejeu.md
 * §12, issue #193) and the trash of §12.4.
 *
 * Four are always visible - experience, status, link and the period - and four
 * live behind « Plus de filtres »: protocol, persona, criterion and, since
 * issue #145, the RETAINED noyau (noyau.md §9.2, N26). The first three name an
 * OBJECT and never a version: « une session fige les versions qu'elle a
 * jouées, la liste les résout vers le protocole, le persona et les critères
 * d'aujourd'hui ». So an operator filters on the protocol they know, and gets
 * every session that ran any version of it.
 *
 * `noyau` offers the six noyaux, and a session with no retained noyau satisfies
 * none of them (N26). The SCHEMA was posed by the contract of #147, and the list
 * applies it since the lot of Sessions (#152), on the `retained` of the line's
 * five codes - the very value its pill shows, by the rule of the gap threshold
 * since issue #169 (N75).
 *
 * The period is two DAYS and not two instants, because the control is a date
 * pair and a session is looked for by the day it happened. The server turns
 * them into a half-open range, so a `to` of the 12th holds the whole of the
 * 12th - the trap a bare string comparison walks into.
 *
 * `trash` is exclusive rather than additive: « Afficher la corbeille » SWAPS
 * the list (§12.4), it does not mix deleted rows in among the live ones.
 */
export const sessionFiltersSchema = z
  .object({
    experienceId: z.string().min(1).optional(),
    status: sessionStatusSchema.optional(),
    linkId: z.string().min(1).optional(),
    from: sessionDaySchema.optional(),
    to: sessionDaySchema.optional(),
    protocolId: z.string().min(1).optional(),
    personaId: z.string().min(1).optional(),
    criterionId: z.string().min(1).optional(),
    /** The retained noyau (noyau.md §9.2, §3.8): the sessions that retained this one. */
    noyau: noyauCodeSchema.optional(),
    /** The review (relecture.md §13): the sessions whose review holds something, or nothing. */
    review: z.enum(['with', 'without']).optional(),
    /** The replay (rejeu.md section 12): the sessions Claude played, or those a person played. */
    replay: replayFilterSchema.optional(),
    /** The NPC (npc.md §13.1, issue #210): the replays of this NPC, named by its identifier. */
    npc: replayNpcFilterSchema.optional(),
    /** The origin (vitrine.md section 8.2, issue #192): the sessions the synchronisation brought up from the prod, or those born here. */
    origin: sessionOriginSchema.optional(),
    /**
     * The search of §12.1 / recherche-sessions.md §4: what the bar types, trimmed,
     * two characters at least. The server hands it to `parseSearchQuery`, which
     * splits it into words and ignores a one-character one (§3), so a `q` that
     * passes here can still read as « no search ».
     */
    q: z.string().trim().min(2).max(200).optional(),
    /** The trash of §12.4: its rows INSTEAD of the live ones, never beside them. */
    trash: z.boolean().default(false),
  })
  .strict();
export type SessionFilters = z.infer<typeof sessionFiltersSchema>;
